import React from 'react';

import { FlexBox } from '../box';
import { Skeleton } from '../skeleton';
import { Tabs } from '../tabs';
import { SignatureSvg, SignatureSvgProps } from './draw-signature';
import { TypeSignature } from './type-signature';

export interface Point {
    x: number;
    y: number;
}

export interface SignatureFieldData {
    lines?: readonly (readonly Point[])[];
    dataUrl: string;
}

export type SignatureMode = 'draw' | 'type' | 'both';

export interface SignatureProps extends SignatureSvgProps {
    mode?: SignatureMode;
    drawTabLabel?: string;
    typeTabLabel?: string;
    typeInfoText: string;
    typeWatermark?: string;
    textFieldLabel: string;
    textFieldPlaceholder: string;
    disabled?: boolean;
    error?: React.ReactNode;
    loading?: React.ReactNode;
    required?: boolean;
    value?: SignatureFieldData;
}

export const Signature = (props: SignatureProps) => {
    const {
        mode = 'both',
        height,
        width,
        clearLabel,
        onChange,
        placeholder,
        label,
        drawTabLabel,
        typeTabLabel,
        textFieldLabel,
        textFieldPlaceholder,
        typeInfoText,
        typeWatermark,
        disabled,
        error,
        required,
        value,
        loading
    } = props;

    const showDraw = mode === 'draw' || mode === 'both';
    const showType = mode === 'type' || mode === 'both';

    const drawPanel = React.useMemo(
        () =>
            showDraw ? (
                <SignatureSvg
                    width={width}
                    height={height}
                    clearLabel={clearLabel}
                    onChange={onChange}
                    placeholder={placeholder}
                    label={label}
                    disabled={disabled}
                    error={error}
                    required={required}
                    initialState={value?.lines}
                />
            ) : null,
        [
            showDraw,
            width,
            height,
            clearLabel,
            onChange,
            placeholder,
            label,
            disabled,
            error,
            required,
            value?.lines
        ]
    );

    const typePanel = React.useMemo(
        () =>
            showType ? (
                <TypeSignature
                    width={width}
                    height={height}
                    infoText={typeInfoText}
                    textFieldLabel={textFieldLabel}
                    textFieldPlaceholder={textFieldPlaceholder}
                    onChange={onChange}
                    watermark={typeWatermark}
                    disabled={disabled}
                    error={error}
                    required={required}
                    initialState={value}
                />
            ) : null,
        [
            showType,
            width,
            height,
            typeInfoText,
            textFieldLabel,
            textFieldPlaceholder,
            onChange,
            typeWatermark,
            disabled,
            error,
            required,
            value
        ]
    );

    const items = React.useMemo(
        () =>
            mode === 'both'
                ? [
                      {
                          label: drawTabLabel ?? '',
                          content: () => drawPanel,
                          'data-role': 'draw-signature-tab'
                      },
                      {
                          label: typeTabLabel ?? '',
                          content: () => typePanel,
                          'data-role': 'type-signature-tab'
                      }
                  ]
                : [],
        [mode, drawTabLabel, typeTabLabel, drawPanel, typePanel]
    );

    if (loading) {
        return (
            <FlexBox stack gap={2}>
                <Skeleton width={width} height={36} variant="rectangular" />
                <Skeleton width={width} height={height} variant="rectangular" />
            </FlexBox>
        );
    }

    if (mode === 'draw') {
        return <FlexBox stack>{drawPanel}</FlexBox>;
    }

    if (mode === 'type') {
        return <FlexBox stack>{typePanel}</FlexBox>;
    }

    return (
        <FlexBox stack gap={1}>
            <Tabs items={items} />
        </FlexBox>
    );
};
